import type { HotelModelSpec } from '@elara/domain/models/HotelModelSpec';
import type { WidgetImage } from '@elara/domain/models/schemas/image-schemas';

import type { HotelsContainer } from '#containers/HotelsContainer.js';
import type { ClientDocumentHandle } from '#firebase/types.js';
import type { Hotel } from '#models/HotelModel.js';

import { getErrorReporter } from '#lib/error-reporting.js';
import { createState } from '#lib/signals-utils.js';
import { parseDocumentId } from '#lib/utils.js';

export function createHotelGuideState(hotels: HotelsContainer) {
  const state = createState({
    handle: undefined as ClientDocumentHandle<HotelModelSpec> | undefined,
    /** Images fetched from the image search API */
    fetchedImages: [] as WidgetImage[],

    get hotel(): Hotel | undefined {
      return this.handle?.data as Hotel | undefined;
    },

    get isLoading(): boolean {
      return !(this.handle?.isReady ?? false);
    },

    /** Finds a hotel by its ID */
    findHotel(hotelId: string) {
      try {
        const id = parseDocumentId(hotelId) ?? hotelId;
        this.handle = hotels.ClientHotel.get({ id });
      } catch (error) {
        getErrorReporter().captureException(error);
        throw error;
      }
    },

    /** Sets the fetched images from the image search API */
    setFetchedImages(images: WidgetImage[]) {
      this.fetchedImages = images;
    },

    /** Resets the state */
    reset() {
      this.handle = undefined;
      this.fetchedImages = [];
    },
  });

  return state;
}

/** The main reactive store for all core hotel guide functionality. All properties are signals transparent to the user. */
export type HotelGuideState = ReturnType<typeof createHotelGuideState>;

